import React, { useState, useRef, useEffect } from 'react';
import { Toast } from 'primereact/toast';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { useOperatorProfileStore } from '../stateManagers/stores/operatorProfileStore';

const TopBar: React.FC = () => {
  const [settingsVisible, setSettingsVisible] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const toast = useRef<Toast>(null);

  // Zustand store
  const { profile, loading, error, fetchProfile, clearError } = useOperatorProfileStore();

  // Fetch profile on mount
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Show error toast if there's an error
  useEffect(() => {
    if (error && toast.current) {
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: error,
        life: 3000
      });
      clearError();
    }
  }, [error, clearError]);

  const getInitials = (name?: string) => {
    if (!name) return 'U';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Extract data
  const operatorName = profile?.operator?.name;
  const operatorRole = profile?.operator?.role;
  const branchName = profile?.branch_name || 'Main Branch';

  return (
    <>
      <Toast ref={toast} />

      <div
        style={{
          height: '75px',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 40px',
          gap: '24px',
          boxShadow: '0 1px 3px rgba(14, 34, 93, 0.08)',
          position: 'relative',
          zIndex: 50
        }}
      >
        {/* Left side - Branch Name (Simple Text) */}
        <div style={{ 
          flex: '1', 
          maxWidth: '500px',
          display: 'flex',
          alignItems: 'center'
        }}>
          <div style={{
            fontSize: '17px',
            fontWeight: '600',
            color: '#0e225d',
            letterSpacing: '-0.01em'
          }}>
            {loading ? 'Loading branch...' : branchName}
          </div>
        </div>

        {/* Right side - Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* Settings Button */}
          <button
            onClick={() => setSettingsVisible(true)}
            style={{
              width: '44px',
              height: '44px',
              background: '#F5F7FA',
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#E8EEF7';
              e.currentTarget.style.borderColor = '#fbb21f';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(14, 34, 93, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#F5F7FA';
              e.currentTarget.style.borderColor = '#E5E7EB';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <i className="pi pi-cog" style={{ fontSize: '18px', color: '#0e225d' }}></i>
          </button>

          {/* Notification Button */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setNotificationsOpen(!notificationsOpen)}
              style={{
                width: '44px',
                height: '44px',
                background: '#F5F7FA',
                border: '1px solid #E5E7EB',
                borderRadius: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#E8EEF7';
                e.currentTarget.style.borderColor = '#fbb21f';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(14, 34, 93, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#F5F7FA';
                e.currentTarget.style.borderColor = '#E5E7EB';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <i className="pi pi-bell" style={{ fontSize: '18px', color: '#0e225d' }}></i>
              <span
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  width: '10px',
                  height: '10px',
                  background: '#ef4444',
                  borderRadius: '50%',
                  border: '2px solid white',
                  animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                }}
              ></span>
            </button>

            {/* Notifications Dropdown (unchanged) */}
            {notificationsOpen && (
              <div style={{
                position: 'absolute',
                top: '54px',
                right: '0',
                width: '320px',
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 10px 40px rgba(14, 34, 93, 0.2)',
                border: '1px solid #E5E7EB',
                overflow: 'hidden',
                animation: 'slideDown 0.2s ease',
                zIndex: 1000
              }}>
                <div style={{
                  padding: '16px 20px',
                  borderBottom: '1px solid #E5E7EB',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#0e225d' }}>
                    Notifications
                  </h3>
                  <span style={{
                    background: '#ef4444',
                    color: 'white',
                    fontSize: '11px',
                    fontWeight: '600',
                    padding: '2px 8px',
                    borderRadius: '12px'
                  }}>
                    3 New
                  </span>
                </div>
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {[
                    { icon: 'pi-user', text: 'New student registration', time: '5 min ago', color: '#0e225d' },
                    { icon: 'pi-calendar', text: 'Exam scheduled for tomorrow', time: '1 hour ago', color: '#fbb21f' },
                    { icon: 'pi-bell', text: 'Fee payment reminder', time: '2 hours ago', color: '#16a34a' }
                  ].map((notif, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: '16px 20px',
                        borderBottom: idx < 2 ? '1px solid #f3f4f6' : 'none',
                        cursor: 'pointer',
                        transition: 'background 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#F5F7FA'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                        <div style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '10px',
                          background: `${notif.color}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <i className={`pi ${notif.icon}`} style={{ fontSize: '16px', color: notif.color }}></i>
                        </div>
                        <div style={{ flex: 1 }}>
                          <p style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#0e225d', fontWeight: '500' }}>
                            {notif.text}
                          </p>
                          <span style={{ fontSize: '12px', color: '#9ca3af' }}>{notif.time}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{
                  padding: '12px 20px',
                  borderTop: '1px solid #E5E7EB',
                  textAlign: 'center'
                }}>
                  <button 
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#0e225d',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      padding: '4px',
                      transition: 'color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#fbb21f'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#0e225d'}
                  >
                    View all notifications
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Divider */}
          <div style={{ width: '1px', height: '40px', background: '#E5E7EB' }}></div>

          {/* User Profile */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              cursor: 'pointer',
              padding: '8px 12px',
              borderRadius: '12px',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#F5F7FA'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            {loading ? (
              <div style={{
                width: '44px',
                height: '44px',
                borderRadius: '12px',
                background: '#E8EEF7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <i className="pi pi-spin pi-spinner" style={{ fontSize: '18px', color: '#0e225d' }}></i>
              </div>
            ) : (
              <div style={{
                width: '44px',
                height: '44px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '16px',
                fontWeight: '700',
                boxShadow: '0 2px 8px rgba(14, 34, 93, 0.3)',
                position: 'relative',
                border: '2px solid #fbb21f'
              }}>
                {getInitials(operatorName)}
                <span style={{
                  position: 'absolute',
                  bottom: '0',
                  right: '0',
                  width: '12px',
                  height: '12px',
                  background: '#16a34a',
                  borderRadius: '50%',
                  border: '2px solid white'
                }}></span>
              </div>
            )}

            <div style={{ textAlign: 'left' }}>
              <div style={{
                color: '#0e225d',
                fontSize: '15px',
                fontWeight: '600',
                marginBottom: '2px'
              }}>
                {loading ? 'Loading...' : operatorName || 'Unknown User'}
              </div>
              {/* <div style={{
                color: '#6b7280',
                fontSize: '13px',
                textTransform: 'capitalize',
              }}>
                {loading ? '...' : operatorRole || 'Operator'}
              </div> */}
            </div>

            <i className="pi pi-chevron-down" style={{ fontSize: '12px', color: '#9ca3af', marginLeft: '4px' }}></i>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </>
  );
};

export default TopBar;